import type React from 'react';
import { useEffect, useReducer } from 'react';
import {
  clearStoredSessionLocally,
  onSignedOutElsewhere,
  readSessionItem,
  wasSignedOutElsewhere,
  writeSessionItem,
} from '../helpers/session-scoped-storage';
import { UserContext } from './user.context';
import {
  initialState,
  loadPersistedUserState,
  safeParseUserState,
  USER_STATE_STORAGE_KEY,
  USER_STATE_UPDATED_EVENT,
  UserActionTypes,
  userReducer,
} from './user.state';

type UserManagerProviderProps = { children: React.ReactNode };

const localState = loadPersistedUserState();

export const UserProvider = ({ children }: UserManagerProviderProps) => {
  const [state, dispatch] = useReducer(userReducer, localState || initialState);

  useEffect(() => {
    writeSessionItem(USER_STATE_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // A public session is per-tab, so a sibling's sign-out can't reach this tab's copy — clear it
  // here and drop to unauthenticated, as shared durable storage did for an ordinary session.
  useEffect(() => {
    const signOut = () => {
      clearStoredSessionLocally();
      dispatch({ type: UserActionTypes.Reset });
    };
    const unsubscribe = onSignedOutElsewhere(signOut);
    // Re-check after the listener is live: a sibling that signed out between the module-load
    // check and this registration was seen by neither, so catch it here or via the broadcast.
    if (wasSignedOutElsewhere()) {
      signOut();
    }
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleUserStateUpdated = () => {
      const updated = safeParseUserState(readSessionItem(USER_STATE_STORAGE_KEY));
      dispatch({ type: UserActionTypes.UpdateUser, payload: updated });
    };
    window.addEventListener(USER_STATE_UPDATED_EVENT, handleUserStateUpdated);
    return () => window.removeEventListener(USER_STATE_UPDATED_EVENT, handleUserStateUpdated);
  }, []);

  return <UserContext.Provider value={{ state, dispatch }}>{children}</UserContext.Provider>;
};
